import { PRIVACY_CONFIG } from "./privacy-config";

export type RelayerListing = {
  id: string;
  name: string;
  url: string;
  region?: string;
  trusted: boolean;
};

type RegistryDocument = {
  version: 1;
  relayers: unknown[];
};

export async function loadRelayerRegistry(): Promise<RelayerListing[]> {
  const listings: RelayerListing[] = [];
  if (PRIVACY_CONFIG.trustedRelayerUrl) {
    listings.push({
      id: "zero1",
      name: "Zero1",
      url: normalizeCustomRelayerUrl(PRIVACY_CONFIG.trustedRelayerUrl),
      region: "Official",
      trusted: true,
    });
  }

  try {
    const response = await fetch(PRIVACY_CONFIG.relayerRegistryUrl, { cache: "no-store" });
    if (!response.ok) throw new Error(`registry request failed (${response.status})`);
    const document = await response.json() as RegistryDocument;
    if (document.version !== 1 || !Array.isArray(document.relayers)) throw new Error("invalid relayer registry");
    for (const entry of document.relayers) {
      const listing = parseCommunityListing(entry);
      if (!listings.some((candidate) => candidate.id === listing.id || candidate.url === listing.url)) {
        listings.push(listing);
      }
    }
  } catch {
    // The trusted configured relayer remains available if the optional
    // community registry cannot be loaded.
  }
  return listings;
}

function parseCommunityListing(value: unknown): RelayerListing {
  const entry = value as Record<string, unknown> | null;
  if (!entry || typeof entry !== "object") throw new Error("invalid relayer entry");
  const id = String(entry.id ?? "");
  const name = String(entry.name ?? "");
  const region = entry.region === undefined ? undefined : String(entry.region);
  if (!/^[a-z0-9-]{1,32}$/.test(id)) throw new Error("invalid relayer id");
  if (id === "zero1" || id === "custom") throw new Error("reserved relayer id");
  if (!name || name.length > 48) throw new Error("invalid relayer name");
  if (region && region.length > 48) throw new Error("invalid relayer region");
  return { id, name, region, url: normalizeCustomRelayerUrl(String(entry.url ?? "")), trusted: false };
}

export function normalizeCustomRelayerUrl(raw: string): string {
  const parsed = new URL(raw);
  const local = parsed.hostname === "localhost" || parsed.hostname === "127.0.0.1";
  if (parsed.protocol !== "https:" && !(local && parsed.protocol === "http:")) {
    throw new Error("relayer URL must use HTTPS");
  }
  if (parsed.username || parsed.password || parsed.search || parsed.hash) throw new Error("invalid relayer URL");
  parsed.pathname = parsed.pathname.replace(/\/+$/, "");
  return parsed.toString().replace(/\/+$/, "");
}
